import React from 'react';

import { useTranslation } from 'react-i18next';

import { useNode, useEditor } from '@craftjs/core';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { Badge, Box, IconButton } from '@mui/material';
import {
  calcNetNodeSize,
  calcParentSize,
  isValidNum,
  calcRenderTimes,
} from '@common/helpers/domManipulationHelpers';

const SizeStyleDivide = ({ propKey = 'size' }) => {
  const { query } = useEditor();

  const {
    actions: { setProp },
    propValue,
    parentId,
  } = useNode(({ data }) => {
    return {
      propValue: data?.props[propKey],
      parentId: data?.parent,
    };
  });

  const { t } = useTranslation();

  const parentSize =
    propKey === 'width'
      ? calcParentSize({ parentId, query })
      : calcNetNodeSize(query);

  const renderTimes = calcRenderTimes({
    parentSize: parentSize[propKey],
    propSize: propValue,
  });

  const onIncrease = ({ renderTimes, parentSize }) => {
    if (isNaN(renderTimes) || !isValidNum(parentSize)) return;
    const newRenderTimes = Number(renderTimes) + 1;
    const newValue = Math.floor(parentSize / newRenderTimes);
    setProp((props) => {
      props[propKey] = newValue;
    }, 500);
  };

  const onDecrease = ({ renderTimes, parentSize }) => {
    if (!isValidNum(renderTimes) || !isValidNum(parentSize)) return;
    const newRenderTimes = Number(renderTimes) - 1;
    const newValue = Math.floor(parentSize / newRenderTimes);
    setProp((props) => {
      props[propKey] = newValue;
    }, 500);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '40px',
        alignContent: 'center',
        alignItems: 'center',
        paddingX: '4px',
        border: (theme) => `1px solid ${theme.palette.action.disabled}`,
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: (theme) => theme.palette.text.primary,
        },
        borderRadius: '3px',
        boxSizing: 'border-box',
      }}
    >
      <Badge
        badgeContent={renderTimes}
        color='secondary'
        sx={{
          '& .MuiBadge-badge': {
            padding: 0,
            width: '13px',
            minWidth: 0,
            height: '15px',
            fontSize: '10px',
          },
        }}
      >
        <ContentCutIcon
          sx={{
            height: '20px',
            width: '20px',
            color: (theme) => theme.palette.action.disabled,
            transform: propKey === 'height' && 'rotate(90deg)',
          }}
        />
      </Badge>

      <Box sx={{ pl: '4px' }}>
        <IconButton
          sx={{ p: 0 }}
          onClick={() =>
            onIncrease({ renderTimes, parentSize: parentSize[propKey] })
          }
        >
          <ExpandLessIcon sx={{ height: '20px', width: '20px' }} />
        </IconButton>
        <IconButton
          sx={{ p: 0 }}
          onClick={() =>
            onDecrease({ renderTimes, parentSize: parentSize[propKey] })
          }
        >
          <ExpandMoreIcon sx={{ height: '20px', width: '20px' }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default SizeStyleDivide;
